import { type FileKind, foldSearchText, type RevisionState } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Loader,
  Menu,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowAutofitWidth,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconInfoCircle,
  IconMaximize,
  IconMinimize,
  IconMinus,
  IconPlus,
  IconPrinter,
  IconRefresh,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import {
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Documents.module.css';
import { formatFileSize, servedRevisionState, strictestState, watermarkText } from './library.js';
import { FileKindIcon } from './parts.js';
import {
  archiveTooLargeToPreview,
  type CssRuleLike,
  createLatestGate,
  createRangeReader,
  PRINT_PART_SCOPE,
  PRINT_PART_SELECTOR,
  previewEngine,
  previewPrintError,
  probeServedState,
  type SheetModel,
  scopedPrintCss,
  ViewerError,
  zoomText,
} from './viewer-core.js';

/** Yakınlaştırma basamakları (%50–%200; plan Task 15). */
export const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export interface FileViewerHandle {
  /**
   * Basılacak sayfaları hazırlar (`[data-print-root]`, her sayfada altlık) ve temizleme işlevini
   * döner. Yazdırma önizleyicinin çizdiği sayfadan yapılır; Office gerekmez [sorular #20].
   */
  preparePrint(footer: string): Promise<() => void>;
  /** Tam ekran (Open Document); `Esc` ile çıkılır. */
  fullscreen(): void;
  /** Tam ekrandan çıkar (ör. ekranın uyarısı görünsün diye; inceleme S3). */
  exitFullscreen(): void;
}

export interface FileViewerProps {
  file: { id: string; name: string; size: number; kind: FileKind };
  /** `Range` destekli adres (web: ofis dosya ucu; gemi: `felix-file://`). */
  url: string;
  /** Revizyonun durumu: yürürlükte değilse filigran (ADR 0028 #30). */
  state: RevisionState;
  canDownload: boolean;
  canPrint: boolean;
  onDownload: () => void;
  onPrint: () => void;
  /**
   * Dosya ucunun bildirdiği durum (`x-felix-revision-state`; ADR 0032 #87): detaydan daha sıkıysa
   * filigran ondandır; ekran indirme ve basmayı da buna göre kısar (inceleme WF-1).
   */
  onServedState?: ((state: RevisionState | null) => void) | undefined;
  ref?: Ref<FileViewerHandle>;
}

/** Bir sonraki ya da önceki yakınlaştırma basamağı. */
export function stepZoom(zoom: number, direction: 1 | -1): number {
  if (direction > 0) {
    return ZOOM_STEPS.find((step) => step > zoom + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? 2;
  }
  return [...ZOOM_STEPS].reverse().find((step) => step < zoom - 0.001) ?? ZOOM_STEPS[0];
}

/** Motorun dışarı verdiği işlemler: baskı ve belge içi arama. */
interface EngineApi {
  print(root: HTMLElement, urls: string[]): Promise<void>;
  /** `isCurrent`: arama hâlâ son sorgu mu; değilse sonuç uygulanmaz (inceleme F12). */
  find(query: string, isCurrent?: () => boolean): Promise<number>;
  findStep(direction: 1 | -1): Promise<number>;
}

/**
 * Yakınlaştırma kipi: `auto` (açılış) sayfa sığıyorsa %100, sığmıyorsa genişliğe sığdırır;
 * `fit` her zaman genişliğe sığdırır; `manual` seçilen basamak.
 */
type ZoomMode = 'auto' | 'fit' | 'manual';

/** Kipe göre ölçek: sığdırma ölçeği `fitScale` (sayfa genişliği / alan). */
export function effectiveScale(mode: ZoomMode, zoom: number, fitScale: number | null): number {
  if (mode === 'manual' || fitScale === null) return zoom;
  return mode === 'fit' ? fitScale : Math.min(1, fitScale);
}

interface EngineProps {
  url: string;
  title: string;
  zoom: number;
  fit: ZoomMode;
  watermark: string | null;
  onReady: (info: { count: number; approximate: boolean; scale?: number }) => void;
  onError: (code: string) => void;
  onPage: (page: number) => void;
  /** Dosya ucunun `x-felix-revision-state` başlığı. */
  onServed: (header: string | null) => void;
  /** iframe'in içindeki tuş (Esc, Ctrl+F, Tab): üst pencereye ulaşmaz (inceleme S4). */
  onFrameKey: (event: globalThis.KeyboardEvent) => void;
  register: (api: EngineApi | null) => void;
}

const engines = () => import('./viewer-engines.js');

/** Hata kodu; beklenmeyen hata konsola yazılır (gemide ana süreç günlüğüne düşer). */
function errorCodeOf(error: unknown): string {
  if (error instanceof ViewerError) return error.code;
  console.error('Önizleme açılamadı', String(error), (error as Error)?.stack);
  return 'GENERIC';
}

// ---------------------------------------------------------------------------------------------
// PDF: pdf.js, Range ile; yalnız görünen sayfalar çizilir
// ---------------------------------------------------------------------------------------------

type PdfHandle = Awaited<ReturnType<Awaited<ReturnType<typeof engines>>['openPdf']>>;

const PAGE_GAP = 16;

/** 1'den `count`'a sayfa numaraları. */
function pageNumbers(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index + 1);
}

function PdfPage({
  handle,
  page,
  scale,
  size,
  root,
  watermark,
  highlighted,
}: {
  handle: PdfHandle;
  page: number;
  scale: number;
  size: { width: number; height: number };
  root: HTMLDivElement | null;
  watermark: string | null;
  highlighted: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [own, setOwn] = useState<{ width: number; height: number } | null>(null);
  const actual = own ?? size;

  useEffect(() => {
    const element = box.current;
    if (!element || !root) return;
    const observer = new IntersectionObserver(
      (entries) => setVisible(entries.some((entry) => entry.isIntersecting)),
      { root, rootMargin: '150% 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    if (!visible || own) return;
    let live = true;
    void handle.pageSize(page).then((value) => {
      if (live && (value.width !== size.width || value.height !== size.height)) setOwn(value);
    });
    return () => {
      live = false;
    };
  }, [visible, own, handle, page, size]);

  useEffect(() => {
    const target = canvas.current;
    if (!target) return;
    if (!visible) {
      // Görünmeyen sayfanın çizimi bırakılır: 300 sayfalık el kitabı belleği doldurmaz
      target.width = 0;
      target.height = 0;
      return;
    }
    const ratio = Math.min(globalThis.devicePixelRatio || 1, 2);
    const task = handle.render(page, target, scale, ratio);
    task.promise.catch(() => undefined);
    return () => task.cancel();
  }, [visible, handle, page, scale]);

  return (
    <div
      ref={box}
      className={classes.pdfPage}
      data-page={page}
      data-highlighted={highlighted || undefined}
      style={{ width: actual.width * scale, height: actual.height * scale }}
    >
      <canvas ref={canvas} className={classes.pdfCanvas} aria-hidden />
      {watermark ? (
        <span className={classes.pageWatermark} data-watermark aria-hidden>
          {watermark}
        </span>
      ) : null}
    </div>
  );
}

function PdfEngine({
  url,
  zoom,
  fit,
  watermark,
  onReady,
  onError,
  onPage,
  onServed,
  register,
}: EngineProps) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [handle, setHandle] = useState<PdfHandle | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [width, setWidth] = useState(0);
  const [matches, setMatches] = useState<number[]>([]);
  const [matchIndex, setMatchIndex] = useState(0);
  const callbacks = useRef({ onReady, onError, onServed });
  callbacks.current = { onReady, onError, onServed };

  useEffect(() => {
    let live = true;
    let opened: PdfHandle | null = null;
    const controller = new AbortController();
    void (async () => {
      try {
        const { openPdf } = await engines();
        const value = await openPdf(url, {
          signal: controller.signal,
          // Açıldıktan sonra parça okunamadı: sayfa boş kalmaz, hata ve Retry (inceleme F1)
          onFailure: (error) => {
            if (live) callbacks.current.onError(errorCodeOf(error));
          },
        });
        opened = value;
        if (!live) {
          value.destroy();
          return;
        }
        callbacks.current.onServed(value.servedState);
        const first = await value.pageSize(1);
        if (!live) return;
        setSize(first);
        setHandle(value);
      } catch (error) {
        if (live) callbacks.current.onError(errorCodeOf(error));
      }
    })();
    return () => {
      live = false;
      controller.abort();
      opened?.destroy();
    };
  }, [url]);

  useEffect(() => {
    const element = root;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, [root]);

  const scale = effectiveScale(
    fit,
    zoom,
    size && width > 0 ? Math.max(0.25, (width - 48) / size.width) : null,
  );

  useEffect(() => {
    if (handle && size) {
      callbacks.current.onReady({ count: handle.numPages, approximate: false, scale });
    }
  }, [scale, size, handle]);

  // Belge içi aramanın sayfa metinleri (katlanmış): her sorguda yeniden okunmaz (inceleme F12)
  const texts = useMemo(() => {
    void handle;
    return new Map<number, string>();
  }, [handle]);

  const scrollToPage = useCallback((page: number) => {
    const element = scroller.current?.querySelector<HTMLElement>(`[data-page="${page}"]`);
    element?.scrollIntoView({ block: 'start' });
  }, []);

  useEffect(() => {
    if (!handle) {
      register(null);
      return;
    }
    register({
      async print(target, urls) {
        const { pdfPageImage } = await engines();
        for (let page = 1; page <= handle.numPages; page += 1) {
          const src = await pdfPageImage(handle, page);
          urls.push(src);
          const wrap = target.ownerDocument.createElement('div');
          wrap.className = watermark ? 'print-page wm' : 'print-page';
          wrap.setAttribute('data-print-part', '');
          const image = target.ownerDocument.createElement('img');
          image.src = src;
          image.alt = '';
          wrap.append(image);
          target.append(wrap);
          await image.decode().catch(() => undefined);
        }
      },
      async find(query, isCurrent = () => true) {
        const needle = foldSearchText(query.trim());
        if (!needle) {
          setMatches([]);
          return 0;
        }
        const pages: number[] = [];
        for (let page = 1; page <= handle.numPages; page += 1) {
          // Yeni sorgu geldiyse tarama bırakılır; sayfa metni bir kez okunur (inceleme F12)
          if (!isCurrent()) return 0;
          let text = texts.get(page);
          if (text === undefined) {
            text = foldSearchText(await handle.text(page));
            texts.set(page, text);
          }
          if (text.includes(needle)) pages.push(page);
        }
        if (!isCurrent()) return 0;
        setMatches(pages);
        setMatchIndex(0);
        if (pages[0]) scrollToPage(pages[0]);
        return pages.length;
      },
      async findStep(direction) {
        if (matches.length === 0) return 0;
        const next = (matchIndex + direction + matches.length) % matches.length;
        setMatchIndex(next);
        const page = matches[next];
        if (page) scrollToPage(page);
        return next + 1;
      },
    });
    return () => register(null);
  }, [handle, register, watermark, matches, matchIndex, scrollToPage, texts]);

  const onScroll = () => {
    const element = scroller.current;
    if (!element || !size) return;
    const pageHeight = size.height * scale + PAGE_GAP;
    const page = Math.floor((element.scrollTop + element.clientHeight / 3) / pageHeight) + 1;
    onPage(Math.max(1, Math.min(page, handle?.numPages ?? 1)));
  };

  return (
    <div
      ref={(element) => {
        scroller.current = element;
        setRoot(element);
      }}
      className={classes.pdfScroller}
      onScroll={onScroll}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan önizleme klavyeyle kaydırılır
      tabIndex={0}
    >
      {handle && size
        ? pageNumbers(handle.numPages).map((page) => (
            <PdfPage
              key={page}
              handle={handle}
              page={page}
              scale={scale}
              size={size}
              root={root}
              watermark={watermark}
              highlighted={matches.length > 0 && matches[matchIndex] === page}
            />
          ))
        : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// DOCX ve XLSX: sandbox'lı, betiksiz iframe
// ---------------------------------------------------------------------------------------------

function FrameEngine({
  url,
  title,
  kind,
  zoom,
  fit,
  watermark,
  sheet,
  onSheets,
  onReady,
  onError,
  onPage,
  onServed,
  onFrameKey,
  register,
  sheetNote,
}: EngineProps & {
  kind: 'docx' | 'xlsx';
  sheet: number;
  onSheets: (names: string[]) => void;
  /** Sınırı aşan XLSX sayfasının notu (inceleme F11). */
  sheetNote: (sheet: SheetModel) => string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [frameDoc, setFrameDoc] = useState<Document | null>(null);
  const [width, setWidth] = useState(0);
  const callbacks = useRef({ onReady, onError, onSheets, onPage, onServed, onFrameKey, sheetNote });
  callbacks.current = { onReady, onError, onSheets, onPage, onServed, onFrameKey, sheetNote };

  useEffect(() => {
    const element = host.current;
    if (!element) return;
    let live = true;
    let frame: HTMLIFrameElement | null = null;
    // Dokümandan çıkılınca bütün dosyanın inişi kesilir (64 MiB'a kadar; inceleme F10)
    const controller = new AbortController();
    void (async () => {
      try {
        const module = await engines();
        frame = module.createFrame(element.ownerDocument, title);
        frame.className = classes.frame ?? '';
        element.append(frame);
        const [loaded, file] = await Promise.all([
          module.frameReady(frame),
          createRangeReader(url, undefined, { signal: controller.signal }).whole(),
        ]);
        if (!live) return;
        callbacks.current.onServed(file.state);
        // Sıkıştırma bombası açılmaz (inceleme M8a3-R21)
        if (archiveTooLargeToPreview(new Uint8Array(file.data))) {
          throw new ViewerError('PREVIEW_TOO_LARGE');
        }
        if (kind === 'docx') {
          await module.renderDocx(file.data, loaded);
        } else {
          const sheets: SheetModel[] = await module.readWorkbook(file.data);
          if (!live) return;
          module.renderSheets(loaded, sheets, (value) => callbacks.current.sheetNote(value));
          callbacks.current.onSheets(sheets.map((s) => s.name));
        }
        if (!live) return;
        // Önizlemenin içindeki tuşlar üst pencereye ulaşmaz: Esc, Ctrl+F ve Tab buradan
        // iletilir (sandbox aynı köken; iframe'de betik yok, dinleyici üst pencerenindir)
        loaded.addEventListener('keydown', (event) => callbacks.current.onFrameKey(event));
        const view = loaded.defaultView;
        view?.addEventListener('scroll', () => {
          if (kind !== 'docx') return;
          const sections = [...loaded.querySelectorAll<HTMLElement>('section.docx')];
          const zoomed = Number(loaded.body.style.zoom) || 1;
          const middle = (view.scrollY + view.innerHeight / 3) / zoomed;
          const index = sections.findLastIndex((section) => section.offsetTop <= middle);
          callbacks.current.onPage(Math.max(1, index + 1));
        });
        setFrameDoc(loaded);
      } catch (error) {
        if (live) callbacks.current.onError(errorCodeOf(error));
      }
    })();
    return () => {
      live = false;
      controller.abort();
      frame?.remove();
    };
  }, [url, kind, title]);

  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Yakınlaştırma: belge gövdesinde CSS `zoom`; sığdır ilk sayfanın (ya da tablonun) genişliğine
  useEffect(() => {
    if (!frameDoc) return;
    let fitScale: number | null = null;
    if (width > 0) {
      const target =
        frameDoc.querySelector<HTMLElement>('section.docx') ??
        frameDoc.querySelector<HTMLElement>(`[data-sheet="${sheet}"] table`);
      // Doğal genişlik yakınlaştırmasız ölçülür (`zoom` altındaki ölçü ölçeklenebilir). Alan,
      // çerçevenin kaydırma çubuğu düşülmüş genişliğidir (gemi PC'sinde klasik çubuk); sayfa
      // kenar boşluğuyla (iki yanda 16 px) sığar, yatay kayma olmaz
      frameDoc.body.style.zoom = '1';
      const natural = target?.offsetWidth ?? 0;
      const inner = frameDoc.documentElement.clientWidth || width;
      if (natural > 0) fitScale = Math.max(0.25, (inner - 16) / (natural + 32));
    }
    // Tablo (XLSX) açılışta %100 kalır: sütunlar okunur, gerekirse yatay kayar
    const scale = effectiveScale(
      kind === 'xlsx' && fit === 'auto' ? 'manual' : fit,
      zoom,
      fitScale,
    );
    frameDoc.body.style.zoom = String(scale);
    callbacks.current.onReady({
      count:
        kind === 'docx'
          ? Math.max(frameDoc.querySelectorAll('section.docx').length, 1)
          : frameDoc.querySelectorAll('[data-sheet]').length,
      approximate: kind === 'docx',
      scale,
    });
  }, [frameDoc, zoom, fit, width, kind, sheet]);

  useEffect(() => {
    if (!frameDoc || kind !== 'xlsx') return;
    for (const element of frameDoc.querySelectorAll<HTMLElement>('[data-sheet]')) {
      element.hidden = element.dataset.sheet !== String(sheet);
    }
  }, [frameDoc, sheet, kind]);

  useEffect(() => {
    if (!frameDoc) {
      register(null);
      return;
    }
    const view = frameDoc.defaultView as
      | (Window & { find?: (...args: unknown[]) => boolean })
      | null;
    let query = '';
    let total = 0;
    let index = 0;
    register({
      async print(target) {
        const owner = target.ownerDocument;
        // Belgenin CSS'i olduğu gibi kopyalanmaz: CSSOM'dan süzülür ve basılı parçaya kapsanır;
        // `@page`, `@import`, `@media` atılır (inceleme SEC-1)
        const rules: CssRuleLike[] = [];
        for (const sheet of frameDoc.styleSheets) {
          try {
            rules.push(...(sheet.cssRules as unknown as Iterable<CssRuleLike>));
          } catch {
            // Okunamayan sayfa (başka köken) alınmaz
          }
        }
        const style = owner.createElement('style');
        style.textContent = scopedPrintCss(rules, PRINT_PART_SCOPE);
        target.append(style);
        // XLSX'te sayfa kabı (sınır notu dahil; M8a3-R32); gizli sayfa da basılır
        const parts = [...frameDoc.querySelectorAll<HTMLElement>(PRINT_PART_SELECTOR[kind])];
        for (const part of parts) {
          const wrap = owner.createElement('div');
          wrap.className = `${kind === 'docx' ? 'print-page' : 'print-sheet'}${watermark ? ' wm' : ''}`;
          wrap.setAttribute('data-print-part', '');
          const copy = owner.importNode(part, true);
          copy.removeAttribute('hidden');
          wrap.append(copy);
          target.append(wrap);
        }
      },
      async find(text, isCurrent = () => true) {
        if (!isCurrent()) return 0;
        query = text.trim();
        index = 0;
        view?.getSelection()?.removeAllRanges();
        if (!query) {
          total = 0;
          return 0;
        }
        const needle = foldSearchText(query);
        total = foldSearchText(frameDoc.body.textContent ?? '').split(needle).length - 1;
        if (total > 0) {
          view?.find?.(query, false, false, true);
          index = 1;
        }
        return total;
      },
      async findStep(direction) {
        if (!query || total === 0) return 0;
        view?.find?.(query, false, direction < 0, true);
        index = ((index - 1 + direction + total) % total) + 1;
        return index;
      },
    });
    return () => register(null);
  }, [frameDoc, kind, watermark, register]);

  return (
    <>
      <div ref={host} className={classes.frameHost} />
      {/* Filigran iframe'in dışında: belgenin CSS'i ona erişemez (inceleme SEC-1) */}
      {watermark ? (
        <span className={classes.watermark} data-watermark aria-hidden>
          {watermark}
        </span>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Resim
// ---------------------------------------------------------------------------------------------

function ImageEngine({
  url,
  title,
  zoom,
  fit,
  watermark,
  onReady,
  onError,
  register,
}: EngineProps) {
  useEffect(() => {
    register({
      async print(target) {
        const wrap = target.ownerDocument.createElement('div');
        wrap.className = watermark ? 'print-page wm' : 'print-page';
        wrap.setAttribute('data-print-part', '');
        const image = target.ownerDocument.createElement('img');
        image.src = url;
        image.alt = '';
        wrap.append(image);
        target.append(wrap);
        await image.decode().catch(() => undefined);
      },
      find: async () => 0,
      findStep: async () => 0,
    });
    return () => register(null);
  }, [register, url, watermark]);
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan önizleme klavyeyle kaydırılır
    <div className={classes.imageScroller} tabIndex={0}>
      <img
        src={url}
        alt={title}
        className={classes.previewImage}
        style={
          fit === 'manual' ? { width: `${zoom * 100}%`, maxWidth: 'none' } : { maxWidth: '100%' }
        }
        onLoad={() => onReady({ count: 1, approximate: false })}
        onError={() => onError('PREVIEW_FAILED')}
      />
      {watermark ? (
        <span className={classes.watermark} data-watermark aria-hidden>
          {watermark}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Önizleyici
// ---------------------------------------------------------------------------------------------

/**
 * Önizleyici (gemi-ism-formlari.jpeg; plan M8a Task 15): koyu araç çubuğunda dosya türü, dosya
 * adı, yakınlaştırma (`−`, sığdır, %50–%200, `+`), indirme, yazdırma ve tam ekran; altında
 * kayan sayfalar ve sayfa göstergesi. PDF kesin sayfa sayısıyla, DOCX yaklaşık ("≈"), XLSX
 * sayfa sekmeli. Yürürlükte olmayan revizyonda durum filigranı. `Ctrl+F` belge içi arama
 * (ADR 0028 #37 madde 2): araç çubuğunda kalıcı ikon yoktur (tasarım bağlayıcı).
 */
export function FileViewer({
  file,
  url,
  state,
  canDownload,
  canPrint,
  onDownload,
  onPrint,
  onServedState,
  ref,
}: FileViewerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const v = (key: string, options?: Record<string, unknown>) =>
    t(`documents.viewer.${key}`, options ?? {});
  const root = useRef<HTMLDivElement>(null);
  const engineApi = useRef<EngineApi | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [fit, setFit] = useState<ZoomMode>('auto');
  const [scale, setScale] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<
    { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; code: string }
  >({ kind: 'loading' });
  const [pages, setPages] = useState({ current: 1, count: 0, approximate: false });
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState(0);
  const [finding, setFinding] = useState(false);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ index: number; total: number } | null>(null);
  // Dosya ucunun durumu, okunduğu andaki detay durumuyla: detay değişince düşer (M8a3-R14)
  const [served, setServed] = useState<{
    value: RevisionState | null;
    detail: RevisionState;
  } | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  // Tam ekranı açan öğe: çıkınca odak ona döner (inceleme S4)
  const opener = useRef<HTMLElement | null>(null);
  const state_ = useRef({ expanded, finding });
  state_.current = { expanded, finding };
  // Retry: motor yeniden kurulur (dosya baştan açılır)
  const [attempt, setAttempt] = useState(0);
  const servedNow = served && served.detail === state ? served.value : null;
  const watermark = watermarkText(strictestState(state, servedNow));
  const engine = previewEngine(file.kind, file.size);

  const register = useCallback((api: EngineApi | null) => {
    engineApi.current = api;
  }, []);
  const onReady = useCallback((info: { count: number; approximate: boolean; scale?: number }) => {
    setStatus({ kind: 'ready' });
    setPages((current) => ({ ...current, count: info.count, approximate: info.approximate }));
    if (info.scale !== undefined) setScale(info.scale);
  }, []);
  const onError = useCallback((code: string) => setStatus({ kind: 'error', code }), []);
  const servedCallback = useRef(onServedState);
  servedCallback.current = onServedState;
  const onServed = useCallback((header: string | null) => {
    const value = servedRevisionState(header);
    setServed({ value, detail: stateRef.current });
    servedCallback.current?.(value);
  }, []);

  // Resim `<img>` ile, çok büyük dosya hiç okunmaz: durum küçük bir istekle (M8a3-R15)
  useEffect(() => {
    if (engine !== 'image' && engine !== 'too-large') return;
    void attempt;
    const controller = new AbortController();
    void probeServedState(url, undefined, controller.signal).then((header) => {
      if (!controller.signal.aborted) onServed(header);
    });
    return () => controller.abort();
  }, [engine, url, onServed, attempt]);
  const onPage = useCallback(
    (page: number) =>
      setPages((current) => (current.current === page ? current : { ...current, current: page })),
    [],
  );

  const enterFullscreen = useCallback(() => {
    const active = globalThis.document.activeElement;
    opener.current = active instanceof HTMLElement ? active : null;
    setExpanded(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      async preparePrint(footer: string) {
        const api = engineApi.current;
        // Çok büyük ya da açılamayan dosya burada basılamaz (inceleme M8a3-R12)
        if (!api) throw new ViewerError(previewPrintError(engine, status.kind));
        const { mountPrintRoot } = await engines();
        const urls: string[] = [];
        const holder = globalThis.document.createElement('div');
        await api.print(holder, urls);
        return mountPrintRoot(
          globalThis.document,
          (target) => {
            while (holder.firstChild) target.append(holder.firstChild);
          },
          footer,
          { cleanupUrls: urls, watermark },
        );
      },
      fullscreen() {
        enterFullscreen();
      },
      exitFullscreen() {
        setExpanded(false);
      },
    }),
    [watermark, enterFullscreen, engine, status.kind],
  );

  /** Tam ekranda Tab içeride döner (pencere gibi; inceleme S4). */
  const trapTab = useCallback((event: globalThis.KeyboardEvent, fromFrame: boolean) => {
    const element = root.current;
    if (!element) return;
    const tabbables = [
      ...element.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((node) => node.getClientRects().length > 0);
    const first = tabbables[0];
    const last = tabbables.at(-1);
    if (!first || !last) return;
    const active = fromFrame ? element.querySelector('iframe') : globalThis.document.activeElement;
    const outside = !active || !element.contains(active);
    if (event.shiftKey && (outside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (outside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  // Tam ekrandan Esc ile çıkılır; Tab içeride döner
  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !finding) setExpanded(false);
      if (event.key === 'Tab') trapTab(event, false);
    };
    // Yakalama evresinde: ipucu ve menü Esc'yi kendileri tüketmeden önce
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expanded, finding, trapTab]);

  // Açılınca odak çıkış düğmesine; çıkınca açan öğeye döner (inceleme S4)
  useEffect(() => {
    if (!expanded) return;
    const toggle = () =>
      root.current?.querySelector<HTMLButtonElement>('[data-fullscreen-toggle]') ?? null;
    toggle()?.focus();
    return () => {
      const back = opener.current?.isConnected ? opener.current : toggle();
      back?.focus();
    };
  }, [expanded]);

  /** iframe'den gelen tuş: Ctrl+F aramayı açar, Esc aramayı ya da tam ekranı kapatır. */
  const onFrameKey = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setFinding(true);
      } else if (event.key === 'Escape') {
        if (state_.current.finding) {
          searchGate.current.cancel();
          setFinding(false);
          setFound(null);
        } else if (state_.current.expanded) {
          setExpanded(false);
        }
      } else if (event.key === 'Tab' && state_.current.expanded) {
        trapTab(event, true);
      }
    },
    [trapTab],
  );

  /** Araç çubuğunda sol/sağ ok, Home ve End düğmeler arasında gezer (WAI-ARIA toolbar). */
  const onToolbarKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const buttons = [
      ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
    ];
    const index = buttons.indexOf(globalThis.document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  /** Ctrl+F: belge içi arama çubuğu (önizleyicinin içindeyken). */
  const onViewerKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      setFinding(true);
    }
    if (event.key === 'Escape' && finding) {
      event.stopPropagation();
      closeFind();
    }
  };

  // Belge içi arama: yazarken bekler (250 ms), yalnız son sorgunun sonucu uygulanır (F12)
  const searchGate = useRef(createLatestGate());
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const search = (text: string) => {
    setQuery(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const token = searchGate.current.begin();
    const isCurrent = () => searchGate.current.isCurrent(token);
    if (!text.trim()) {
      void engineApi.current?.find('');
      setFound(null);
      return;
    }
    searchTimer.current = setTimeout(() => {
      void (async () => {
        const total = (await engineApi.current?.find(text, isCurrent)) ?? 0;
        if (isCurrent()) setFound({ index: total > 0 ? 1 : 0, total });
      })();
    }, 250);
  };
  const closeFind = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchGate.current.cancel();
    setFinding(false);
    setFound(null);
  };
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );
  const step = async (direction: 1 | -1) => {
    const index = (await engineApi.current?.findStep(direction)) ?? 0;
    setFound((current) => (current ? { ...current, index } : current));
  };

  const zoomLabel = zoomText(scale, locale);
  const tool = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    disabled = false,
    extra: Record<string, unknown> = {},
  ) => (
    <Tooltip label={label} withArrow withinPortal={!expanded}>
      <ActionIcon
        variant="subtle"
        className={classes.toolButton}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        {...extra}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );

  const engineProps: EngineProps = {
    url,
    title: `${v('toolbar')}: ${file.name}`,
    zoom,
    fit,
    watermark,
    onReady,
    onError,
    onPage,
    onServed,
    onFrameKey,
    register,
  };

  let body: ReactNode;
  if (engine === 'too-large') {
    body = (
      <div className={classes.viewerStatus}>
        <IconInfoCircle size={22} aria-hidden />
        <span>{v('tooLarge', { size: formatFileSize(file.size, locale) })}</span>
        {canDownload ? (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={onDownload}
          >
            {v('download')}
          </Button>
        ) : null}
      </div>
    );
  } else {
    body = (
      <>
        {engine === 'pdf' ? <PdfEngine key={attempt} {...engineProps} /> : null}
        {engine === 'docx' || engine === 'xlsx' ? (
          <FrameEngine
            key={attempt}
            {...engineProps}
            kind={engine}
            sheet={sheet}
            onSheets={(names) => setSheets(names)}
            sheetNote={(model) =>
              v('truncated', {
                rows: model.rows.length,
                columns: model.columns,
                totalRows: model.truncated?.rows ?? model.rows.length,
                totalColumns: model.truncated?.columns ?? model.columns,
              })
            }
          />
        ) : null}
        {engine === 'image' ? <ImageEngine key={attempt} {...engineProps} /> : null}
        {status.kind === 'loading' ? (
          <div className={classes.viewerStatus} role="status">
            <Loader size="sm" />
            <span>{v('loading')}</span>
          </div>
        ) : null}
        {status.kind === 'error' ? (
          <div className={classes.viewerStatus} role="alert">
            <IconAlertTriangle size={22} aria-hidden />
            <span>{t(`documents.errors.${status.code}`, { defaultValue: v('error') })}</span>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => {
                setStatus({ kind: 'loading' });
                setAttempt((value) => value + 1);
              }}
            >
              {t('documents.screen.retry')}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  const pageText = pages.approximate
    ? v('pageApprox', { page: Math.min(pages.current, pages.count), count: pages.count })
    : v('page', { page: Math.min(pages.current, pages.count), count: pages.count });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Ctrl+F önizleyicinin içinde yakalanır
    <div
      ref={root}
      className={classes.viewer}
      data-expanded={expanded || undefined}
      data-kind={file.kind}
      data-status={status.kind}
      onKeyDown={onViewerKey}
      // Tam ekran pencere gibidir: ekran okuyucu altını okumaz, odak içeride (inceleme S4)
      {...(expanded
        ? { role: 'dialog', 'aria-modal': true, 'aria-label': `${v('toolbar')}: ${file.name}` }
        : {})}
    >
      {/* Filigran görseldir (aria-hidden): durum ekran okuyucuya ayrıca yazılır (M8a3-R13) */}
      {watermark ? (
        <span className={classes.srOnly}>{v('stateNotice', { state: watermark })}</span>
      ) : null}
      <div
        role="toolbar"
        aria-label={v('toolbar')}
        className={classes.viewerToolbar}
        onKeyDown={onToolbarKey}
      >
        <span className={classes.viewerFile}>
          <FileKindIcon kind={file.kind} size={24} />
          <span className={classes.viewerName} title={file.name}>
            {file.name}
          </span>
        </span>
        <span className={classes.viewerZoom}>
          {tool(v('zoomOut'), <IconMinus size={18} />, () => {
            setFit('manual');
            setZoom(stepZoom(scale, -1));
          })}
          {tool(
            v('fit'),
            <IconArrowAutofitWidth size={18} />,
            () => setFit((value) => (value === 'fit' ? 'manual' : 'fit')),
            false,
            { 'aria-pressed': fit === 'fit' },
          )}
          <Menu position="bottom" withinPortal={!expanded}>
            <Menu.Target>
              <UnstyledButton
                className={classes.zoomButton}
                aria-label={`${v('zoom')}: ${zoomLabel}`}
              >
                {zoomLabel}
                <IconChevronDown size={14} aria-hidden />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {ZOOM_STEPS.map((value) => (
                <Menu.Item
                  key={value}
                  onClick={() => {
                    setFit('manual');
                    setZoom(value);
                  }}
                >
                  {zoomText(value, locale)}
                </Menu.Item>
              ))}
              <Menu.Item onClick={() => setFit('fit')}>{v('fit')}</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {tool(v('zoomIn'), <IconPlus size={18} />, () => {
            setFit('manual');
            setZoom(stepZoom(scale, 1));
          })}
        </span>
        <span className={classes.viewerActions}>
          {tool(v('download'), <IconDownload size={18} />, onDownload, !canDownload)}
          {tool(
            v('print'),
            <IconPrinter size={18} />,
            onPrint,
            !canPrint || status.kind !== 'ready',
          )}
          {tool(
            expanded ? v('exitFullscreen') : v('fullscreen'),
            expanded ? <IconMinimize size={18} /> : <IconMaximize size={18} />,
            () => (expanded ? setExpanded(false) : enterFullscreen()),
            false,
            { 'data-fullscreen-toggle': true },
          )}
        </span>
      </div>
      {finding ? (
        <search className={classes.findBar} aria-label={v('find')}>
          <TextInput
            size="xs"
            autoFocus
            leftSection={<IconSearch size={14} aria-hidden />}
            aria-label={v('find')}
            placeholder={v('find')}
            value={query}
            onChange={(event) => search(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void step(event.shiftKey ? -1 : 1);
            }}
          />
          <span className={classes.findCount} aria-live="polite">
            {found
              ? found.total > 0
                ? v('matches', { index: found.index, count: found.total })
                : v('noMatches')
              : ''}
          </span>
          <ActionIcon variant="subtle" aria-label={v('findPrevious')} onClick={() => void step(-1)}>
            <IconChevronUp size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" aria-label={v('findNext')} onClick={() => void step(1)}>
            <IconChevronDown size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" aria-label={v('findClose')} onClick={closeFind}>
            <IconX size={16} />
          </ActionIcon>
        </search>
      ) : null}
      <div className={classes.viewerBody}>{body}</div>
      {engine === 'xlsx' && sheets.length > 0 ? (
        <div role="tablist" aria-label={v('sheets')} className={classes.sheetTabs}>
          {sheets.map((name, index) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={index === sheet}
              tabIndex={index === sheet ? 0 : -1}
              className={classes.sheetTab}
              onClick={() => setSheet(index)}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                event.preventDefault();
                const next =
                  (index + (event.key === 'ArrowRight' ? 1 : -1) + sheets.length) % sheets.length;
                setSheet(next);
                const tabs = event.currentTarget.parentElement?.children;
                (tabs?.[next] as HTMLElement | undefined)?.focus();
              }}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      {status.kind === 'ready' && engine !== 'xlsx' && engine !== 'image' && pages.count > 0 ? (
        <span className={classes.pageIndicator} data-approximate={pages.approximate || undefined}>
          {pageText}
          {pages.approximate ? (
            <Tooltip label={v('approximate')} withArrow>
              <span className={classes.approxIcon} role="img" aria-label={v('approximate')}>
                <IconInfoCircle size={13} aria-hidden />
              </span>
            </Tooltip>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
